import { fetchMyBuilds } from '@/api/builds';
import { fetchDeliveryConfig, fetchSessions } from '@/api/sender';
import type { IBuild } from '@/entities/build.entity';
import type { DeliveryProfile, SessionSummary } from '@/entities/sender.entity';
import { useCallback, useEffect, useState } from 'react';

export function useSenderLaunchData(enabled = true) {
	const [builds, setBuilds] = useState<IBuild[]>([]);
	const [sessions, setSessions] = useState<SessionSummary[]>([]);
	const [deliveryProfile, setDeliveryProfile] = useState<DeliveryProfile | null>(null);
	const [buildsLoading, setBuildsLoading] = useState(false);
	const [sessionsLoading, setSessionsLoading] = useState(false);
	const [deliveryProfileLoading, setDeliveryProfileLoading] = useState(false);
	const [deliveryProfileError, setDeliveryProfileError] = useState(false);

	const reload = useCallback(async () => {
		if (!enabled) return;
		setBuildsLoading(true);
		setSessionsLoading(true);
		setDeliveryProfileLoading(true);
		setDeliveryProfileError(false);

		const [buildsResult, sessionsResult, configResult] = await Promise.allSettled([
			fetchMyBuilds(),
			fetchSessions(),
			fetchDeliveryConfig(),
		]);

		if (buildsResult.status === 'fulfilled') {
			setBuilds(buildsResult.value.filter(b => b.hasApk && b.successBuilded));
		} else {
			setBuilds([]);
		}

		if (sessionsResult.status === 'fulfilled') {
			setSessions(sessionsResult.value.items.filter(s => s.status === 'ready'));
		} else {
			setSessions([]);
		}

		if (configResult.status === 'fulfilled') {
			setDeliveryProfile(configResult.value.deliveryProfile);
		} else {
			setDeliveryProfile(null);
			setDeliveryProfileError(true);
		}

		setBuildsLoading(false);
		setSessionsLoading(false);
		setDeliveryProfileLoading(false);
	}, [enabled]);

	useEffect(() => {
		void reload();
	}, [reload]);

	const retryDeliveryProfile = useCallback(async () => {
		setDeliveryProfileLoading(true);
		setDeliveryProfileError(false);
		try {
			const config = await fetchDeliveryConfig();
			setDeliveryProfile(config.deliveryProfile);
		} catch {
			setDeliveryProfile(null);
			setDeliveryProfileError(true);
		} finally {
			setDeliveryProfileLoading(false);
		}
	}, []);

	return {
		builds,
		sessions,
		deliveryProfile,
		buildsLoading,
		sessionsLoading,
		deliveryProfileLoading,
		deliveryProfileError,
		reload,
		retryDeliveryProfile,
	};
}
